import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, fmtDate, kina } from "@/lib/api";

const Row = ({ label, value, strong }) => (
  <div className={`flex justify-between py-2 ${strong ? "border-t-2 border-slate-900 text-lg font-bold" : "border-t border-slate-200"}`}>
    <span>{label}</span>
    <span className="font-mono-num">{value}</span>
  </div>
);

export default function Statement() {
  const { party, partyId, date } = useParams();
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/statement", { params: { party, party_id: partyId, date } }).then((r) => setS(r.data)).catch(() => setS(false));
  }, [party, partyId, date]);
  if (s === null) return <div className="p-10 text-slate-500">Loading statement…</div>;
  if (!s) return <div className="p-10 text-red-600">Statement could not be loaded.</div>;
  const isDriver = s.party === "driver";
  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="mx-auto mb-4 flex max-w-3xl justify-end px-4 print:hidden">
        <button data-testid="print-statement-button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-600">
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
      </div>
      <div data-testid="statement-document" className="mx-auto max-w-3xl bg-white p-8 shadow-xl print:max-w-none print:p-0 print:shadow-none sm:p-12">
        <div className="flex items-start justify-between border-b-4 border-blue-700 pb-6">
          <Logo />
          <div className="text-right">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Platform fee statement</p>
            <p className="mt-1 font-display text-2xl font-bold" data-testid="statement-date">{s.date}</p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Billed to</p>
            <p className="mt-1 text-base font-semibold" data-testid="statement-party-name">{s.name}</p>
            <p className="text-slate-500">{isDriver ? "Comfort Taxi driver" : "Comfort Taxi fleet operator"}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Status</p>
            <p data-testid="statement-status" className={`mt-1 font-semibold ${s.settled ? "text-emerald-700" : "text-red-600"}`}>{s.settled ? `PAID · ${fmtDate(s.settled_at)}` : "AMOUNT OWING"}</p>
          </div>
        </div>
        <table className="mt-8 w-full text-left text-xs">
          <thead><tr className="border-b border-slate-300 uppercase tracking-wider text-slate-500">
            <th className="py-2">Time</th>{!isDriver && <th>Driver</th>}<th>Route</th><th className="text-right">Km</th><th className="text-right">Fare</th>
          </tr></thead>
          <tbody>
            {s.trips.map((t) => (
              <tr key={t.id} className="border-b border-slate-100">
                <td className="py-2">{fmtDate(t.completed_at)}</td>
                {!isDriver && <td>{t.driver_name}</td>}
                <td>{t.pickup.address} → {t.dropoff.address}</td>
                <td className="text-right">{t.distance_km}</td>
                <td className="text-right font-mono-num">{kina(t.fare)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="ml-auto mt-8 max-w-xs text-sm">
          <Row label={`Trips completed`} value={s.trips.length} />
          <Row label="Total fares (cash)" value={kina(s.gross)} />
          <Row label={`Platform fee (${s.fee_pct}%)`} value={<span data-testid="statement-fee">{kina(s.fee)}</span>} strong />
        </div>
        <div className="mt-16 grid grid-cols-2 gap-10 text-xs text-slate-500">
          <div className="border-t border-slate-400 pt-2">Received by (platform)</div>
          <div className="border-t border-slate-400 pt-2">Paid by ({s.name})</div>
        </div>
        <p className="mt-10 text-center text-[10px] text-slate-400">Generated {fmtDate(s.generated_at)} · Comfort Taxi Platform, Port Moresby</p>
      </div>
    </div>
  );
}
